'use client';
/* eslint-disable @next/next/no-img-element -- static brand-refresh assets served from /public bypass the site's Cloudinary image loader */
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ArrowUpRightIcon,
  Bars3Icon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import CartIcon from 'components/icons/cart';
import { brandAsset, brandAssetAt } from 'helpers/brandAssets';
import { useCart } from 'frontastic/hooks';
import AnnouncementBar from './announcement-bar';
import { BUILDER_TOOLS, builderForSection } from './builder-tools';
import CartDrawer from './cart-drawer';
import CtaChevron from './cta-chevron';
import { externalLinkProps } from './external-link';
import MobileMenuDrawer, { MobileMenuCallout } from './mobile-menu-drawer';
import { commonFaqs, matchFaqs } from './search-faqs';
import SearchPanel, { ProductRow, SearchStatusMessage } from './search-panel';
import { POPULAR_PRODUCTS } from './search-popular';
import { MIN_QUERY_LENGTH, selectPanelProducts, usePopularProducts, useSearchSuggestions } from './use-search-products';

// Marketing links, still used for the mobile drawer's "Main" group.
const NAV_ITEMS = ['Stairs', 'Railing', 'Inspiration', 'Resources'];

// The header's quote CTA — distinct from `customQuoteLink` (the PDP/PLP custom-quote CTA),
// which points at a different quote page.
const QUOTE_LINK = 'https://www.viewrail.com/request-your-quote';

// The first-level nav defaults to the marketing links; storefront pages pass the shop categories.
const DEFAULT_NAV_ITEMS: SectionNavItem[] = NAV_ITEMS.map((label) => ({ label, href: '#' }));

export interface SectionNavItem {
  label: string;
  href: string;
  /** Catalog category name to match for the active state when the label is a shortened display name. */
  matches?: string;
}

// The phone sheet shows fewer product rows than the desktop panel's eight, so questions and builders stay in reach.
const MOBILE_PRODUCT_ROWS = 5;

export interface SectionNav {
  /** Section label shown in the subnav, e.g. "Railing". Omit on the home page to hide the switcher. */
  name?: string;
  /** The section's landing page, for breadcrumbs that lead with the section. */
  href?: string;
  /** Subcategory in the subnav to mark active/underlined (matched case-insensitively). */
  activeItem?: string;
  /** Top-level subcategories of the current section. */
  items: SectionNavItem[];
  /** Top-most categories to switch between via the section dropdown (Railing, Stair Treads, …). */
  sections?: SectionNavItem[];
  /** Optional right-aligned CTA button in the subnav (e.g. "Get Started"). */
  cta?: SectionNavItem;
  /** Back link to the marketing site's page for the current section/subcategory, e.g. "Experience Railing". */
  experience?: SectionNavItem;
}

// Phone drawer callouts: the lookbook everywhere, plus the sample or kit that fits the section the
// shopper is in — the Discovery Kit on FLIGHT, glass panel samples on Railing, wood samples on Treads
// and Handrail, and the samples hub anywhere else (home, search, cart).
const LOOKBOOK_CALLOUT: MobileMenuCallout = {
  label: 'Download Our Lookbook',
  href: 'https://www.viewrail.com/lookbook/',
  image: brandAsset('lookbook.jpg'),
  external: true,
};

const sectionCallout = (sectionName?: string): MobileMenuCallout => {
  const name = sectionName?.toLowerCase() ?? '';
  if (name.includes('flight'))
    return {
      label: 'FLIGHT Discovery Kit',
      href: '/flight-discovery-kit/p/900023/',
      image: brandAsset('discovery-kit-box.jpg'),
    };
  if (name.includes('railing'))
    return { label: 'Glass Panel Samples', href: '/glass-panel-samples/p/499603/', image: brandAsset('sample-3.jpg') };
  if (name.includes('tread') || name.includes('handrail'))
    return { label: 'Wood Samples', href: '/samples/', image: brandAsset('sample-2.jpg') };
  return { label: 'Order Samples', href: '/samples/', image: brandAsset('sample-1.jpg') };
};

// `navItems` is the first-level nav (the shop categories). `section` is provided on section pages
// (everything except the home page) to render the secondary navigation row with subcategories.
const BrandMenu = ({
  navItems = DEFAULT_NAV_ITEMS,
  section,
}: {
  navItems?: SectionNavItem[];
  section?: SectionNav;
}) => {
  const router = useRouter();
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const headerRef = useRef<HTMLElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchToggleRef = useRef<HTMLButtonElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  const {
    data: cart,
    isEmpty,
    totalItems: totalCartItems,
    removeItem,
    removeDesign,
    updateItem,
    redeemDiscountCode,
  } = useCart();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Mobile search is its own bottom sheet (not the menu drawer's search view).
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  // Focus the input when the desktop panel or the mobile search drawer opens; close on Escape.
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (searchDrawerOpen) mobileSearchInputRef.current?.focus();
  }, [searchDrawerOpen]);

  const sectionBuilder = builderForSection(section?.name);
  const searchActive = searchOpen || searchDrawerOpen;
  const { items: suggestions, status: suggestionStatus } = useSearchSuggestions(query, searchActive);
  const popularProducts = usePopularProducts(POPULAR_PRODUCTS, searchActive);
  // Product thumbnails stay lazy until the shopper reaches for search (hover, focus or touch on a
  // search button), then load eagerly so they're usually cached by the time the panel opens.
  const [warmImages, setWarmImages] = useState(false);
  const warmSearch = { onPointerEnter: () => setWarmImages(true), onFocus: () => setWarmImages(true) };

  // When the desktop panel closes with focus inside it (Escape, a result link), hand focus back to
  // the toggle; the closed panel is inert, so focus would otherwise drop to <body>.
  const wasSearchOpen = useRef(false);
  useEffect(() => {
    if (wasSearchOpen.current && !searchOpen && headerRef.current?.contains(document.activeElement ?? null)) {
      searchToggleRef.current?.focus();
    }
    wasSearchOpen.current = searchOpen;
  }, [searchOpen]);

  const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;
  const matchedFaqs = hasQuery ? matchFaqs(query) : commonFaqs;
  const panelProducts = selectPanelProducts(query, suggestions, popularProducts);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchDrawerOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    const params = new URLSearchParams({ q: query.trim() });
    router.push(`/search?${params.toString()}`);
    setSearchOpen(false);
    setSearchDrawerOpen(false);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchDrawerOpen(false);
  };

  // Publish the header's live height for sticky content beneath it (the PDP gallery pins to
  // `--header-height`). Measured, because the secondary nav and the search panel change it.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const publish = () =>
      document.documentElement.style.setProperty(
        '--header-height',
        `${Math.round(header.getBoundingClientRect().height)}px`,
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <header ref={headerRef} className="on-dark sticky top-0 z-50 w-full bg-[#212121] font-gt text-white">
      <AnnouncementBar />
      {/* Mobile bar (< xl; six categories no longer fit a 1024px bar): VIEWRAIL | SHOP lockup left, search + hamburger right.
          Cart moved into the drawer; a blue dot on the hamburger signals cart contents. */}
      <div className="flex h-64 items-center justify-between px-24 xl:hidden">
        <div className="flex items-center gap-16">
          <a
            href="https://www.viewrail.com"
            aria-label="Viewrail"
            className="flex min-h-44 items-center transition-opacity hover:opacity-70"
          >
            <img src={brandAssetAt('logo-wordmark.png', 284)} alt="Viewrail" width={142} height={17} />
          </a>
          <span className="h-24 w-px bg-white/40" aria-hidden />
          <a href="/" className="flex min-h-44 items-center text-12 transition-opacity hover:opacity-70">
            SHOP
          </a>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Search"
            onClick={() => setSearchDrawerOpen(true)}
            {...warmSearch}
            className="-my-11 flex size-44 items-center justify-center transition-opacity hover:opacity-70"
          >
            <MagnifyingGlassIcon className="size-22 text-white" />
          </button>
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMobileMenuOpen(true)}
            className="relative -my-11 -mr-9 flex size-44 items-center justify-center transition-opacity hover:opacity-70"
          >
            {totalCartItems > 0 && (
              <span className="absolute right-7 top-8 size-8 rounded-full bg-white ring-2 ring-[#1c1c1c]" aria-hidden />
            )}
            <Bars3Icon className="size-26 text-white" />
          </button>
        </div>
      </div>

      {/* Desktop bar (xl+): 3-column grid (1fr auto 1fr) so the nav is centered in the viewport,
          not in the leftover space between the (wider) logo lockup and the icons. */}
      {/* The side columns are minmax(max-content, 1fr): equal when there's room (so the nav is viewport-centered),
          but never narrower than the logo lockup or the icon cluster as the window shrinks. */}
      <div className="hidden h-64 grid-cols-[minmax(max-content,1fr)_auto_minmax(max-content,1fr)] items-center px-42 xl:grid">
        {/* Brand + SHOP lockup — logo goes to the main site, SHOP to this shop home */}
        <div className="flex items-center gap-16">
          <a
            href="https://www.viewrail.com"
            aria-label="Viewrail"
            className="flex min-h-44 items-center transition-opacity hover:opacity-70"
          >
            <img src={brandAssetAt('logo-wordmark.png', 284)} alt="Viewrail" width={142} height={17} />
          </a>
          <span className="h-24 w-px bg-white/40" aria-hidden />
          <a href="/" className="flex min-h-44 items-center text-12 transition-opacity hover:opacity-70">
            SHOP
          </a>
        </div>

        {/* Primary nav — the shop categories (first level) */}
        {/* Tighter below 2xl so the six items plus the lockup and icons fit a 1280px window. */}
        <nav className="flex items-center justify-center gap-20 2xl:gap-32">
          {navItems.map((item) => {
            const isCurrent = !!section?.name && item.label.toLowerCase() === section.name.toLowerCase();
            const itemClass = `whitespace-nowrap border-b pb-2 text-14 uppercase text-white transition-opacity hover:opacity-70 ${
              isCurrent ? 'border-white' : 'border-transparent'
            }`;
            return (
              <a key={item.label} href={item.href} className={itemClass} aria-current={isCurrent ? 'page' : undefined}>
                {item.label}
              </a>
            );
          })}
        </nav>

        {/* Account / cart / search — wired to the storefront's existing routes. The 20px search glyph sits
            centred in its 44px hit area, so the group is pulled 12px right: the glyph's edge, not the hit
            area's, lands on the padding line, level with the Experience link's arrow in the sub-nav. */}
        <div className="-mr-12 flex items-center justify-end gap-4">
          <a
            href={QUOTE_LINK}
            {...externalLinkProps(QUOTE_LINK)}
            className="mr-8 flex h-32 items-center whitespace-nowrap bg-white px-16 text-12 font-bold uppercase text-[#1c1c1c] transition-opacity hover:opacity-70"
          >
            Get a Quote
          </a>
          <a
            href="/account/"
            aria-label="Account"
            className="flex size-44 items-center justify-center transition-opacity hover:opacity-70"
          >
            <img src={brandAsset('icon-account.png')} alt="" width={23} height={23} />
          </a>
          <button
            type="button"
            aria-label="Cart"
            onClick={() => setCartOpen(true)}
            className="flex size-44 items-center justify-center transition-opacity hover:opacity-70"
          >
            <CartIcon className="size-24 text-white" totalCartItems={totalCartItems} />
          </button>
          <button
            type="button"
            ref={searchToggleRef}
            aria-label={searchOpen ? 'Close search' : 'Search'}
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((open) => !open)}
            {...warmSearch}
            className="flex size-44 items-center justify-center transition-opacity hover:opacity-70"
          >
            {/* Becomes the close control while the panel is open, in the spot the shopper just clicked. */}
            {searchOpen ? (
              <XMarkIcon className="size-22 text-white" />
            ) : (
              <MagnifyingGlassIcon className="size-20 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Secondary navigation — top categories / section subcategories. Collapses smoothly while search is active. */}
      {section && section.items.length > 0 && (
        <div
          className={`overflow-hidden transition-[max-height,opacity] ease-out ${
            searchOpen ? 'max-h-0 opacity-0 duration-300' : 'max-h-60 opacity-100 duration-500'
          }`}
        >
          <div className="border-t border-white/10">
            {/* Desktop: a 1fr / auto / 1fr grid so the item row centres on the viewport rather than in the space
                beside the Experience link. The middle track is minmax(0, auto) so the row can still shrink and scroll. */}
            <div className="flex h-56 items-center px-24 xl:grid xl:grid-cols-[1fr_minmax(0,auto)_1fr] xl:px-42">
              {/* The item row is a scroller at every size. Its inner track is mx-auto, so the items sit centered
                  while they fit and left-align (and scroll) once they overflow — auto margins collapse to zero
                  in an overflowing flex container. The fade over the right edge is the scroll affordance. */}
              <div className="relative flex min-w-0 flex-1 self-stretch xl:col-start-2">
                <div className="flex min-w-0 flex-1 items-stretch overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <div className="flex items-stretch gap-24 pr-48 xl:mx-auto xl:items-center xl:gap-32 xl:pr-0">
                    {section.items.map((item) => {
                      const isActive = section.activeItem?.toLowerCase() === (item.matches ?? item.label).toLowerCase();
                      return (
                        <a
                          key={item.label}
                          href={item.href}
                          className={`flex items-center whitespace-nowrap text-13 uppercase transition-colors hover:text-white ${
                            isActive ? 'text-white' : 'text-white/80'
                          }`}
                        >
                          {/* Underline on the span, so the anchor itself stays centred on the row (level with the
                              Experience link and CTA) and the rule hangs 2px under the text. */}
                          <span className={`border-b pb-2 ${isActive ? 'border-white' : 'border-transparent'}`}>
                            {item.label}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
                <div
                  className="pointer-events-none absolute inset-y-0 right-0 w-48 bg-gradient-to-l from-[#212121] to-transparent xl:hidden"
                  aria-hidden
                />
              </div>
              {/* Desktop right: the online builder for this section ("Build Custom Railing"), so shoppers who'd
                  rather design their own layout get there in one click. See builderForSection. */}
              {sectionBuilder && (
                <a
                  href={sectionBuilder.href}
                  {...externalLinkProps(sectionBuilder.href)}
                  className="group ml-16 hidden min-h-44 shrink-0 items-center gap-8 whitespace-nowrap text-12 font-bold uppercase text-white transition-opacity hover:opacity-80 xl:col-start-3 xl:inline-flex xl:justify-self-end"
                >
                  <sectionBuilder.Icon className="size-18 shrink-0" aria-hidden />
                  <span className="border-b border-transparent pb-2 group-hover:border-white">{sectionBuilder.label}</span>
                  <ArrowUpRightIcon className="mb-2 size-14 shrink-0" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search panel — expands down from the header (Figma "Shop Banner - Search"). Desktop only; phones use the bottom sheet. */}
      <div className="hidden border-t border-white/10 xl:block">
        <SearchPanel
          open={searchOpen}
          query={query}
          onQueryChange={setQuery}
          onSubmit={handleSearchSubmit}
          inputRef={searchInputRef}
          products={panelProducts}
          status={suggestionStatus}
          eagerImages={warmImages || searchActive}
          faqs={matchedFaqs}
          onNavigate={closeSearch}
        />
      </div>

      {/* Mobile menu drawer (< xl): the shop categories, callouts, plus search. */}
      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onCartClick={() => {
          setMobileMenuOpen(false);
          setCartOpen(true);
        }}
        cartCount={totalCartItems}
        experience={section?.experience}
        // The Samples category's live URL when it's in the nav; the drawer falls back to /samples.
        samplesHref={navItems.find((item) => item.label.toLowerCase() === 'samples')?.href}
        callouts={[LOOKBOOK_CALLOUT, sectionCallout(section?.name)]}
        groups={[
          {
            // Category-first: a flat list of the shop categories, matching the desktop nav.
            items: navItems.map((item) => ({ label: item.label, href: item.href })),
          },
        ]}
        query={query}
        onQueryChange={setQuery}
        onSearchSubmit={handleSearchSubmit}
      />

      {/* Mobile search — a bottom sheet that slides up when the header search icon is tapped. */}
      <Drawer
        isOpen={searchDrawerOpen}
        direction="bottom"
        className="on-dark max-h-[85vh] bg-[#212121] xl:hidden"
        onClose={() => setSearchDrawerOpen(false)}
      >
        <div className="flex flex-col gap-16 px-24 pb-32 pt-20 font-gt text-white">
          <div className="flex items-center justify-between">
            <span className="text-12 font-bold uppercase text-white/60">Search</span>
            <button
              type="button"
              aria-label="Close search"
              onClick={() => setSearchDrawerOpen(false)}
              className="transition-opacity hover:opacity-70"
            >
              <XMarkIcon className="size-24" />
            </button>
          </div>

          <form onSubmit={handleSearchSubmit} className="flex items-center gap-12 border-b border-white/20 pb-12">
            <MagnifyingGlassIcon className="size-22 shrink-0 text-white/60" />
            <input
              ref={mobileSearchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search shop.viewrail.com..."
              className="w-full appearance-none border-0 bg-transparent text-18 text-white outline-none placeholder:text-white/50"
            />
          </form>

          {/* Same order as the desktop panel, stacked: products (popular until the shopper types, then
              results), the matching questions, then the builders. Capped lower than desktop so the sheet
              stays scannable on a phone. */}
          <div className="-mx-24 flex max-h-[65vh] flex-col gap-28 overflow-y-auto px-24 pb-8">
            <section className="flex flex-col gap-12">
              <div className="flex items-baseline justify-between">
                <h2 className="text-12 font-bold uppercase text-white/60">
                  {hasQuery ? 'Products' : 'Popular Products'}
                </h2>
              </div>
              {hasQuery && suggestions.length === 0 ? (
                <SearchStatusMessage status={suggestionStatus} term={query.trim()} />
              ) : (
                <ul className="flex flex-col gap-12">
                  {panelProducts.slice(0, MOBILE_PRODUCT_ROWS).map((item) => (
                    <ProductRow key={item.href} item={item} onNavigate={closeSearch} eager={warmImages || searchActive} />
                  ))}
                </ul>
              )}
              {hasQuery && suggestionStatus !== 'loading' && (
                <a
                  href={`/search/?q=${encodeURIComponent(query.trim())}`}
                  onClick={closeSearch}
                  className="flex min-h-44 items-center font-gt-s text-14 font-bold text-white/70 transition-colors hover:text-white"
                >
                  See all results for “{query.trim()}”
                  <CtaChevron />
                </a>
              )}
            </section>

            {/* Questions collapse behind one row so products stay the focus; each question then expands to its answer. */}
            {matchedFaqs.length > 0 && (
              <details className="group/faqs shrink-0 border-y border-white/10">
                <summary className="flex min-h-48 cursor-pointer list-none items-center justify-between text-12 font-bold uppercase text-white/60 [&::-webkit-details-marker]:hidden">
                  Common Questions ({matchedFaqs.length})
                  <ChevronDownIcon aria-hidden className="size-18 transition-transform group-open/faqs:rotate-180" />
                </summary>
                <ul className="flex flex-col pb-8">
                  {matchedFaqs.map((faq) => (
                    <li key={faq.question}>
                      <details className="group">
                        <summary className="flex min-h-44 cursor-pointer list-none items-center py-6 text-15 text-[#468CE4] group-open:text-[#87B5EF] [&::-webkit-details-marker]:hidden">
                          {faq.question}
                        </summary>
                        <p className="whitespace-pre-line pb-12 text-14 leading-[1.5] text-white/70">{faq.answer}</p>
                      </details>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {/* Builders as one sideways-scrolling row of chips; the bleed to the sheet edge signals there's more. */}
            <section className="flex shrink-0 flex-col gap-10">
              <h2 className="text-12 font-bold uppercase text-white/60">Design it yourself</h2>
              <ul className="-mx-24 flex gap-8 overflow-x-auto px-24 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {BUILDER_TOOLS.map(({ label, href, Icon }) => (
                  <li key={label} className="shrink-0">
                    <a
                      href={href}
                      {...externalLinkProps(href)}
                      onClick={closeSearch}
                      className="flex min-h-44 items-center gap-8 whitespace-nowrap border border-white/20 bg-white/[0.06] pl-8 pr-14 text-14 text-white transition-colors hover:bg-[#2B70CA]"
                    >
                      <span className="flex size-28 items-center justify-center bg-[#2B70CA]">
                        <Icon aria-hidden className="size-18" />
                      </span>
                      {label}
                      <ArrowUpRightIcon aria-hidden className="size-14 text-white/50" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </Drawer>

      {/* Cart side-drawer — reuses the storefront's Slideout, driven by useCart(). */}
      <Drawer
        isOpen={cartOpen}
        direction="right"
        className="w-[90%] max-w-380 bg-white"
        onClose={() => setCartOpen(false)}
      >
        <CartDrawer
          cart={cart}
          isEmpty={isEmpty}
          onUpdateItem={updateItem}
          onRemoveItem={removeItem}
          onRemoveDesign={removeDesign}
          onApplyDiscountCode={redeemDiscountCode}
          onClose={() => setCartOpen(false)}
        />
      </Drawer>
    </header>
  );
};

export default BrandMenu;
